import type { StoreApi } from 'zustand';
import DataSourceHelper from '../../../helper/DataSourceHelper';
import DependencyFlowHelper from '../../../helper/DependencyFlowHelper';
import type { DataSourceRecordStoreState } from '../../../stores/DataSourceRecordStore';
import {
  type DataSourceControllerState,
  DependencyFlowStatus,
  type ManuallyTriggeredDataSourceType,
} from '../types';
import getDataSourceDependencyFlowAction from './dataSourceDependencyFlowAction';
import getDataSourceIntraDependenciesActions from './dataSourceIntraDependenciesActions';

/**
 * these actions are business login driven,
 * when you need an action in store related to a data source, but it is not a simple setter or getter
 * adds a new action here and uses it in your component
 */
const getDataSourceActions = ({
  get,
  set,
  setPreviewTriggeredDataSource,
  getDataSourceRecordsStore,
  setManuallyTriggeredDataSource,
  setRunTriggeredDataSource,
  removeDataSourceFromGlobalState,
}: {
  set: StoreApi<DataSourceControllerState>['setState'];
  get: StoreApi<DataSourceControllerState>['getState'];
  removeDataSourceFromGlobalState: (dataSourceId: string) => void;
  getDataSourceRecordsStore: StoreApi<DataSourceRecordStoreState>['getState'];
  setPreviewTriggeredDataSource: (id: string, value: boolean) => void;
  setManuallyTriggeredDataSource: (id: string, value: ManuallyTriggeredDataSourceType) => unknown;
  setRunTriggeredDataSource: (id: string, value: boolean) => void;
}) => {
  const {
    setDataSourceIntraDependencies,
    setDataSourceIntraDependenciesSingle,
    removeDataSourceIntraDependencies,
  } = getDataSourceIntraDependenciesActions(set);

  const {
    setDataSourcesDependencyFlow,
    setDataSourceInDependencyFlow,
    removeDataSourceFromDependencyFlow,
  } = getDataSourceDependencyFlowAction(set, get);

  const dataSourceActions = {
    triggerManualDataSource: (id, value) => {
      const intraDependencies = get().dataSourceIntraDependencies;
      const dependencyFlow = get().dataSourceDependencyFlow;
      const completeDataSourceDependencyFlow = DependencyFlowHelper.getDependencyFlowStatus({
        dependencyFlowInstance: dependencyFlow,
        entities: {
          [value.dataSourceId]: {
            status: DependencyFlowStatus.Staging,
          },
        },
        intraDependencies,
      });

      // remove id from completeDataSourceDependencyFlow
      const { [value.dataSourceId]: _, ...rest } = completeDataSourceDependencyFlow;

      setDataSourcesDependencyFlow({ entities: rest });

      setManuallyTriggeredDataSource(id, value);
    },
    previewDataSource: (id, value) => {
      if (!value) {
        return setPreviewTriggeredDataSource(id, value);
      }

      // we are building a dependency graph
      // because we want to know the latest dependencies of this data source when the preview is triggered
      const intraDependencies = DataSourceHelper.getPreviewDataSourceTentativeDependencies(
        id,
        getDataSourceRecordsStore,
      );

      const dependencyFlow = get().dataSourceDependencyFlow;

      // now we have put all the data sources in to staging which would be required to fulfill the preview input
      const completeDataSourceDependencyFlow = DependencyFlowHelper.getDependencyFlowStatus({
        dependencyFlowInstance: dependencyFlow,
        entities: {
          [id]: {
            status: DependencyFlowStatus.Staging,
          },
        },
        intraDependencies,
      });

      // remove id from completeDataSourceDependencyFlow
      const { [id]: _, ...rest } = completeDataSourceDependencyFlow;
      setDataSourcesDependencyFlow({ entities: rest });
      setPreviewTriggeredDataSource(id, value);
      // setting run to false, because after previewing a datasource in the preview panel we want to now use the preview data
      setRunTriggeredDataSource(id, false);
    },
    saveAndRunDataSource: (id, value) => {
      setRunTriggeredDataSource(id, value);
    },
    removeDataSourceFromStore: (id: string) => {
      // TODO: check for delete data source
      removeDataSourceFromGlobalState(id);
      removeDataSourceFromDependencyFlow({ entityId: id });
      removeDataSourceIntraDependencies({ entityId: id });
    },
    removeDependencyFlow: (entityId: string) => {
      removeDataSourceFromDependencyFlow({ entityId });
    },
    addDependencyFlow: (entityId, status) => {
      setDataSourceInDependencyFlow({ entityId, status });
    },
    addIntraDependencies: (entities) => {
      setDataSourceIntraDependencies({
        dependencyGraph: entities,
      });
    },
    addDependencyFlows: (entities) => {
      setDataSourcesDependencyFlow({ entities });
    },
    addIntraDependency: (entityId, dependencies) => {
      setDataSourceIntraDependenciesSingle({
        entityId,
        dependencies,
      });

      // now that dependencies are changed, check if this datasource is non staging,
      const dependencyFlow = get().dataSourceDependencyFlow;
      if (!DependencyFlowHelper.isDependencyStaging(dependencyFlow, entityId)) {
        const intraDependencies = get().dataSourceIntraDependencies;
        // then we will filter out all the dependencies that are in unstaged,
        // and put them to staging to fetch their data,
        // intent is to get all the dependencies that are unstaged
        const completeDataSourceDependencyFlow = DependencyFlowHelper.getDependencyFlowStatus({
          dependencyFlowInstance: dependencyFlow,
          entities: {
            [entityId]: {
              status: DependencyFlowStatus.Staging,
            },
          },
          intraDependencies,
        });
        // remove id from completeDataSourceDependencyFlow because it adds itself
        const { [entityId]: _, ...rest } = completeDataSourceDependencyFlow;

        // because this datasource is automatic,
        // after fetching data from new dependencies, it will automatically refetch itself
        setDataSourcesDependencyFlow({ entities: rest });
      }
    },
  };

  return dataSourceActions;
};

export default getDataSourceActions;
